import { MissingIndexedShapeError } from '@takeshape/errors';
import { ensureQuery, findShapeAtPath, isObjectSchema, returnShapeToSchema } from '@takeshape/schema';
import { parse, print, visit } from 'graphql';
import { JSONPath as jsonPath } from 'jsonpath-plus';
import { appendDefinitions, createSchemaInfoVisitor, ensureIds, getFragmentsUsed, getOperation, setSelectionSet, visitWithSchemaInfo } from "./ast-utils.js";
import { getGraphQLQueryString } from "./graphql-helpers.js";
import { jsonPathToArray } from "./pagination/index.js";
function getPredicate(queryConfig) {
    if (queryConfig.ignoreFields) {
        const blacklist = new Set(queryConfig.ignoreFields);
        return ({ propName }) => !propName || !blacklist.has(propName);
    }
}
export function getIndexingQueryString(projectSchema, queryConfig, queryPathToItems, queryArgs) {
    let args = queryArgs;
    if (queryConfig.args) {
        args = { ...queryConfig.args, ...args };
    }
    // Default max depth is 2 but if we have selectionSet use 1 to create a base query to override
    const maxQueryDepth = queryConfig.maxDepth ?? (queryConfig.selectionSet ? 1 : 2);
    const queryString = getGraphQLQueryString(projectSchema, queryConfig.query, {
        predicate: getPredicate(queryConfig),
        maxQueryDepth,
        args,
        includeDeprecated: queryConfig.includeDeprecated ?? false,
        onlyFormProps: false
    });
    const { name } = ensureQuery(projectSchema, queryConfig.query);
    if (queryConfig.selectionSet) {
        const shouldMerge = queryConfig.maxDepth !== undefined;
        const customSelectionDoc = parse(queryConfig.selectionSet, {
            noLocation: true
        });
        const customSelectionSetNode = getOperation(customSelectionDoc)?.selectionSet;
        const baseQueryDoc = parse(queryString, { noLocation: true });
        const queryDoc = appendDefinitions(setSelectionSet(baseQueryDoc, [name, ...queryPathToItems], customSelectionSetNode, shouldMerge), getFragmentsUsed(customSelectionDoc));
        const schemaInfoVisitor = createSchemaInfoVisitor(projectSchema);
        const visitor = visitWithSchemaInfo(schemaInfoVisitor, ensureIds(projectSchema, schemaInfoVisitor, {
            idField: queryConfig.idField,
            shapeId: true
        }));
        return print(visit(queryDoc, visitor));
    }
    return queryString;
}
function isJsonValue(value) {
    return value !== undefined;
}
export async function* paginator({ projectSchema, queryConfig, connector, paginationHelper, errorCallback, firstQueryCallback }) {
    const { name, query } = ensureQuery(projectSchema, queryConfig.query);
    const returnShapeSchema = returnShapeToSchema(projectSchema, query.shape);
    const queryPathToItems = jsonPathToArray(paginationHelper.itemsPath);
    const indexedShape = findShapeAtPath(projectSchema, returnShapeSchema, queryPathToItems);
    if (!indexedShape) {
        throw new MissingIndexedShapeError(queryConfig.query);
    }
    if (!isObjectSchema(indexedShape.schema)) {
        throw new Error(`Paginated shape ${indexedShape.name} is not an object`);
    }
    const { itemsPath, hasMore } = paginationHelper;
    let queryString = getIndexingQueryString(projectSchema, queryConfig, queryPathToItems, paginationHelper.getArgs(undefined));
    if (firstQueryCallback) {
        firstQueryCallback(queryString);
    }
    while (true) {
        const params = { query: queryString };
        const res = await connector(params);
        if (res.errors?.length) {
            errorCallback?.(params, res, 'queryError');
            break;
        }
        const json = res.data?.[name];
        const queryData = isJsonValue(json) && jsonPath({ json, path: itemsPath, wrap: false });
        if (!queryData || !Array.isArray(queryData)) {
            errorCallback?.(params, res, 'noData');
            break;
        }
        for (const item of queryData) {
            yield item;
        }
        const data = res.data?.[name];
        if (!hasMore(data)) {
            break;
        }
        queryString = getIndexingQueryString(projectSchema, queryConfig, queryPathToItems, paginationHelper.getArgs(data));
    }
}
